import { SkeletonText } from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { type ReactNode } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { RowIcon } from './RowIcon';
import { flattenBudgetTree } from './helpers/misc';
import { type BreakdownTree } from '../../../budgets-by-dimensions/models/budgetBreakdown';
import { type DimensionData } from '../../../budgets-by-dimensions/models/dimensionData';
import {
  getPercentageClassName,
  Percentage,
} from '../../../components/Percentage';
import { getSpentBreakdownRatio } from '../../../models/breakdown';

export const BudgetsOverviewListV2 = ({
  budgetName,
  breakdowns,
  dimensionData,
  isLoading,
  selectedItemId,
  onSelectItem,
}: {
  budgetName: string;
  breakdowns: BreakdownTree;
  dimensionData: DimensionData;
  isLoading: boolean;
  selectedItemId?: string;
  onSelectItem?: (itemId: string) => void;
}) => {
  const { t } = useTranslation('global');

  // Build the list items from breakdowns using the shared utility
  const listItems = flattenBudgetTree(
    breakdowns,
    dimensionData,
    {
      budgetName,
      // No collapsed IDs - show everything expanded
    },
    t,
  ).map((row) => ({
    id: row.id,
    name: row.label,
    usedRatio: getSpentBreakdownRatio(row.breakdown),
    isSelected: row.id === (selectedItemId || 'root'),
    depth: row.depth,
    dimension: row.dimension,
    dimensionIcon: <RowIcon row={row} />,
  }));

  const handleSelectBudget = (id: string) => {
    // Call the selection handler to filter the data
    if (onSelectItem) {
      onSelectItem(id);
    }
  };

  return (
    <div className="box mb-24 max-h-max w-[260px] overflow-y-auto p-0 pb-8">
      <div className="text-content-primary title-xl px-24 py-16">
        {t('budget.overviewPage.yourBudgets')}
      </div>
      <div className="separator mb-8" />
      {isLoading ? (
        <Loading />
      ) : (
        <div>
          {listItems.map((item) => (
            <ListItem
              key={item.id}
              name={item.name}
              usedRatio={item.usedRatio}
              isSelected={item.isSelected}
              depth={item.depth}
              dimensionIcon={item.dimensionIcon}
              onClick={() => handleSelectBudget(item.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Loading = () => {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center gap-8 px-8">
        <div className="flex w-32 items-center justify-center">
          <SkeletonText className="h-16 w-16" />
        </div>
        <div className="grow">
          <SkeletonText className="mb-4 h-16 w-full" />
          <SkeletonText className="h-16 w-[88%]" />
        </div>
      </div>
      <div className="px-8 pl-48">
        <SkeletonText className="mb-4 h-16 w-full" />
        <SkeletonText className="h-16 w-[50%]" />
      </div>
      <div className="px-8 pl-48">
        <SkeletonText className="mb-4 h-16 w-full" />
        <SkeletonText className="h-16 w-[76%]" />
      </div>
      <div className="px-8 pl-48">
        <SkeletonText className="mb-4 h-16 w-full" />
        <SkeletonText className="h-16 w-[80%]" />
      </div>
    </div>
  );
};

const ListItem = ({
  name,
  usedRatio,
  isSelected,
  depth,
  dimensionIcon,
  onClick,
}: {
  name: string;
  usedRatio: number;
  isSelected: boolean;
  depth: number;
  dimensionIcon: ReactNode;
  onClick: () => void;
}) => {
  const isSubItem = depth > 0;
  // Calculate padding based on depth: root = 24px, then +16px per level
  const paddingLeft = 24 + depth * 16;

  return (
    <div
      role="button"
      aria-label={name}
      tabIndex={0}
      className={classNames(
        'hover:bg-background-primary-hover relative flex cursor-pointer items-center gap-8 px-8 py-8',
        isSelected &&
          'bg-background-primary-selected hover:bg-background-primary-selected hover:cursor-default',
      )}
      style={{ paddingLeft: `${paddingLeft}px` }}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          onClick();
        }
      }}
    >
      {isSelected && (
        <div className="bg-background-primary-brand-default absolute top-0 left-0 h-full w-[2px]" />
      )}
      <div className="flex items-center">{dimensionIcon}</div>
      <div className="flex flex-col">
        <div
          className={classNames(
            'text-content-primary',
            isSubItem && 'body-m',
            !isSubItem && 'title-m',
          )}
        >
          {name}
        </div>
        <div
          className={classNames(
            getPercentageClassName({ over: usedRatio > 1 }),
            isSubItem && 'body-m',
            !isSubItem && 'title-m',
          )}
        >
          <Percentage ratio={usedRatio} />
        </div>
      </div>
    </div>
  );
};
